import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";

import {
  Typography,
  Alert,
  Box,
  Chip,
  Button,
  Skeleton,
  useMediaQuery,
} from "@mui/material";

import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";

import { useTheme } from "@mui/material/styles";

import { getLegalFilesById } from "../../services/LegalFileService";
import { downloadAttachment, downloadLegalFileDocument } from "../../services/LegalFileAttachmentService";
import { downloadNotificationCertificateFile } from "../../services/NotificationCertificateService";
import { downloadFilePost } from "../../services/ReportService";

import LoadingState from "../General/LoadingState";
import { useAuth } from "../../context/AuthContext";
import AppDialog from "../General/Dialog/AppDialog";
import { useToast } from "../../context/ToastContext";

import LegalFileHeader from "./Detail/LegalFileHeader";
import TabOperaciones from "./Detail/TabOperaciones";
import TabSaldosOrdenes from "./Detail/TabSaldosOrdenes";
import TabCedulas from "./Detail/TabCedulas";
import TabPartes from "./Detail/TabPartes";
import TabDemanda from "./Detail/TabDemanda";
import TabEscritos from "./Detail/TabEscritos";
import TabAdjuntos from "./Detail/TabAdjuntos";
import TabLiquidacion from "./Detail/TabLiquidacion";
import GlassCard from "./Detail/GlassCard";

export default function LegalFileDetail() {
  const { showToast } = useToast();
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";

  const [legalFile, setLegalFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [value, setValue] = useState("1");

  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Cantidad real de liquidaciones (activa + versiones), la reporta la pestaña.
  const [liqCount, setLiqCount] = useState(null);


  const fetchLegalFile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLegalFilesById(id);
      setLegalFile(res.data.data);
      setError("");
    } catch (err) {
      if (err.response?.status === 404) {
        setError("No se encontró el expediente.");
        setLegalFile(null);
        return;
      }
      setError("Error al cargar el expediente.");
      setLegalFile(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLegalFile();
  }, [fetchLegalFile]);

  const handleChangeTab = (_, newValue) => setValue(newValue);

  const openPreviewFor = async (fetchFn) => {
    setPreviewLoading(true);
    try {
      const res = await fetchFn();
      const blob = new Blob([res.data], { type: "application/pdf" });
      setPreviewUrl(URL.createObjectURL(blob));
      setOpenPreview(true);
    } catch {
      showToast("No se pudo cargar el archivo.", "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  const operations = useMemo(() => {
    const ops = legalFile?.operations || [];
    return ops.slice().sort((a, b) => {
      const dateA = a.date ? new Date(a.date).toISOString().split("T")[0] : "";
      const dateB = b.date ? new Date(b.date).toISOString().split("T")[0] : "";
      if (dateA !== dateB) return dateB.localeCompare(dateA);

      const aHasPresented = a.presented && a.presented.trim() !== "";
      const bHasPresented = b.presented && b.presented.trim() !== "";
      if (aHasPresented && !bHasPresented) return 1;
      if (!aHasPresented && bHasPresented) return -1;
      return 0;
    });
  }, [legalFile]);

  const paymentOrders = useMemo(() => {
    const arr = legalFile?.payment_orders || [];
    return arr.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [legalFile]);

  const judicialHistory = useMemo(() => {
    const arr = legalFile?.judicial_account_history ?? [];
    return arr
      .slice()
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  }, [legalFile]);

  const notificationCertificates = useMemo(() => {
    const arr = legalFile?.notification_certificates || [];
    return arr
      .slice()
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [legalFile]);

  const parties = legalFile?.parties || [];
  const attachments = legalFile?.attachments || [];
  const documents = legalFile?.documents || [];
  const lawsuit = legalFile?.lawsuits || null;

  const isExternalFile = legalFile?.type === "external";
  const canSeeLiquidation = isExternalFile
    ? hasPermission("ver liquidaciones comunes")
    : hasPermission("ver liquidaciones");

  const liquidationCount = liqCount ?? documents.filter((d) => d.document_type === "liquidation").length;

  const tabLabel = (text, count) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 850, textTransform: "none" }} noWrap>
        {text}
      </Typography>
      <Chip
        size="small"
        label={count}
        sx={{
          height: 20,
          borderRadius: 999,
          fontWeight: 900,
          bgcolor: "rgba(0,0,0,0.06)",
          "& .MuiChip-label": { px: 0.9, fontSize: 12 },
        }}
      />
    </Box>
  );

  if (loading) {
    return <LoadingState text="Cargando expediente…" />;
  }

  if (!legalFile) {
    return (
      <Box sx={{ mt: 2, px: { xs: 1, sm: 0 } }}>
        {error ? (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        ) : (
          <Typography color="text.secondary">
            No hay información del expediente para mostrar.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: "hidden", px: { xs: 1, sm: 0 } }}>
      <Grid container spacing={2} sx={{ mt: 1 }}>

        <Grid size={{ xs: 12 }}>
          <LegalFileHeader legalFile={legalFile} onRefresh={fetchLegalFile} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TabContext value={value}>
            {!isMobile && (
              <GlassCard sx={{ p: 0 }}>
                <TabList
                  onChange={handleChangeTab}
                  variant="scrollable"
                  scrollButtons={false}
                  sx={{
                    px: 0.5,
                    "& .MuiTab-root": {
                      minHeight: 50,
                      textTransform: "none",
                      fontWeight: 900,
                      px: 1.2,
                    },
                    "& .MuiTabs-flexContainer": { gap: 0.5 },
                    "& .MuiTabs-indicator": { height: 3, borderRadius: 999 },
                  }}
                >
                  <Tab label={tabLabel("Operaciones", operations.length)} value="1" disabled={!hasPermission("ver operacion")} />
                  <Tab label={tabLabel("Saldos y Órdenes", judicialHistory.length + paymentOrders.length)} value="2" disabled={!hasPermission("ver cuentas judiciales detalle") && !hasPermission("ver detalle orden de pago")} />
                  <Tab label={tabLabel(isExternalFile ? "Liq. Común" : "Liquidación", liquidationCount)} value="9" disabled={!canSeeLiquidation} />
                  <Tab label={tabLabel("Cédulas", notificationCertificates.length)} value="4" disabled={!hasPermission("ver cedulas notificacion detalle")} />
                  <Tab label={tabLabel("Partes", parties.length)} value="5" />
                  <Tab label={tabLabel("Título", lawsuit ? 1 : 0)} value="6" disabled={!hasPermission("ver demandas detalle")} />
                  <Tab label={tabLabel("Escritos", attachments.length)} value="7" disabled={!hasPermission("ver archivos adjuntos expedientes detalle")} />
                  <Tab label={tabLabel("Adjuntos", attachments.length + documents.length + notificationCertificates.length + (lawsuit ? 1 : 0))} value="8" />
                </TabList>
              </GlassCard>
            )}

            {isMobile && (
              <Box
                sx={{
                  position: "sticky",
                  top: 72,
                  zIndex: 10,
                  mt: 1.2,
                  mb: 1.2,
                  overflowX: "auto",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.7,
                    px: 0.2,
                    py: 0.5,
                    width: "max-content",
                  }}
                >
                  {[
                    { v: "1", label: "Operaciones", count: operations.length, perm: "ver operacion" },
                    { v: "2", label: "Saldos y Órdenes", count: judicialHistory.length + paymentOrders.length, perm: "ver cuentas judiciales detalle" },
                    { v: "9", label: isExternalFile ? "Liq. Común" : "Liquidación", count: liquidationCount, perm: null, hidden: !canSeeLiquidation },
                    { v: "4", label: "Cédulas", count: notificationCertificates.length, perm: "ver cedulas notificacion detalle" },
                    { v: "5", label: "Partes", count: parties.length, perm: null },
                    { v: "6", label: "Título", count: lawsuit ? 1 : 0, perm: "ver demandas detalle" },
                    { v: "7", label: "Escritos", count: attachments.length, perm: "ver archivos adjuntos expedientes detalle" },
                    { v: "8", label: "Adjuntos", count: attachments.length + documents.length + notificationCertificates.length + (lawsuit ? 1 : 0), perm: null },
                  ]
                    .filter(({ perm, hidden }) => !hidden && (!perm || hasPermission(perm)))
                    .map(({ v, label, count }) => {
                      const active = value === v;
                      return (
                        <Box
                          key={v}
                          onClick={() => setValue(v)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.6,
                            px: 1.4,
                            py: 0.7,
                            borderRadius: 999,
                            cursor: "pointer",
                            userSelect: "none",
                            flexShrink: 0,
                            transition: "all .15s ease",
                            bgcolor: active
                              ? "primary.main"
                              : isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                            color: active ? "#fff" : "text.primary",
                            boxShadow: active ? "0 4px 14px rgba(25,118,210,0.30)" : "none",
                          }}
                        >
                          <Typography sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1, color: "inherit" }}>
                            {label}
                          </Typography>
                          <Box
                            sx={{
                              fontSize: 11,
                              fontWeight: 900,
                              lineHeight: "18px",
                              px: 0.65,
                              borderRadius: 999,
                              bgcolor: active
                                ? "rgba(255,255,255,0.22)"
                                : isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.10)",
                              color: "inherit",
                            }}
                          >
                            {count}
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              </Box>
            )}

            {hasPermission("ver operacion") && (
              <TabPanel value="1" sx={{ p: 0, mt: { xs: 0, md: 1.5 } }}>
                <TabOperaciones
                  operations={operations}
                  legalFile={legalFile}
                  onRefresh={fetchLegalFile}
                />
              </TabPanel>
            )}

            <TabPanel value="2" sx={{ p: 0, mt: { xs: 0, md: 1.5 } }}>
              <TabSaldosOrdenes
                paymentOrders={paymentOrders}
                judicialHistory={judicialHistory}
                legalFileId={id}
                legalFile={legalFile}
                onRefresh={fetchLegalFile}
              />
            </TabPanel>

            {hasPermission("ver cedulas notificacion detalle") && (
              <TabPanel value="4" sx={{ p: 0, mt: { xs: 0, md: 1.5 } }}>
                <TabCedulas
                  notificationCertificates={notificationCertificates}
                  legalFile={legalFile}
                  onRefresh={fetchLegalFile}
                  onPreviewCertificate={(id) => openPreviewFor(() => downloadNotificationCertificateFile(id))}
                />
              </TabPanel>
            )}

            {hasPermission("ver operacion") && (
              <TabPanel value="5" sx={{ p: 0, mt: { xs: 0, md: 1.5 } }}>
                <TabPartes parties={parties} />
              </TabPanel>
            )}

            {hasPermission("ver demandas detalle") && (
              <TabPanel value="6" sx={{ p: 0, mt: { xs: 0, md: 1.5 } }}>
                <TabDemanda
                  lawsuit={lawsuit}
                  onPreviewLawsuit={(lawsuitId) => openPreviewFor(() => downloadFilePost(`lawsuit/${lawsuitId}/download`))}
                />
              </TabPanel>
            )}

            {hasPermission("ver archivos adjuntos expedientes detalle") && (
              <TabPanel value="7" sx={{ p: 0, mt: { xs: 0, md: 1.5 } }}>
                <TabEscritos
                  attachments={attachments}
                  legalFile={legalFile}
                  onRefresh={fetchLegalFile}
                  onPreviewAttachment={(attachmentId) => openPreviewFor(() => downloadAttachment(attachmentId))}
                />
              </TabPanel>
            )}

            <TabPanel value="8" sx={{ p: 0, mt: { xs: 0, md: 1.5 } }}>
              <TabAdjuntos
                attachments={attachments}
                documents={documents}
                notificationCertificates={notificationCertificates}
                lawsuit={lawsuit}
                onPreviewAttachment={(attachmentId) => openPreviewFor(() => downloadAttachment(attachmentId))}
                onPreviewDocument={(documentId) => openPreviewFor(() => downloadLegalFileDocument(documentId))}
                onPreviewCertificate={(certId) => openPreviewFor(() => downloadNotificationCertificateFile(certId))}
                onPreviewLawsuit={(lawsuitId) => openPreviewFor(() => downloadFilePost(`lawsuit/${lawsuitId}/download`))}
                legalFile={legalFile}
                onDocumentsChanged={fetchLegalFile}
              />
            </TabPanel>

            <TabPanel value="9" sx={{ p: 0, mt: { xs: 0, md: 1.5 } }}>
              <TabLiquidacion
                legalFile={legalFile}
                onCountChange={setLiqCount}
                onRefresh={fetchLegalFile}
              />
            </TabPanel>

          </TabContext>
        </Grid>

        <AppDialog
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          title="Vista previa del archivo"
          maxWidth="lg"
          fullScreenMobile
          fullScreenBreakpoint="md"
          stickyHeader
          contentSx={{ p: 0 }}
          paperSx={{ borderRadius: isMobile ? 0 : 4, overflow: "hidden" }}
          actions={
            !isMobile ? (
              <Button
                onClick={() => setOpenPreview(false)}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Cerrar
              </Button>
            ) : null
          }
        >
          {previewLoading ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant="rounded" height={isMobile ? 420 : 680} />
            </Box>
          ) : previewUrl ? (
            <iframe
              src={previewUrl}
              style={{
                width: "100%",
                height: isMobile ? "calc(100vh - 56px)" : "82vh",
                border: 0,
              }}
              title="preview"
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography>No se pudo cargar el archivo.</Typography>
            </Box>
          )}
        </AppDialog>

      </Grid>
    </Box>
  );
}
